import { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'
import classNames from 'classnames'
import withStyles from 'beam/dist/styles'
import IconButton from 'beam/dist/IconButton'
import BeamIcon from 'beam/dist/BeamIcon'
import Snackbar from '../../../../Common/NguiSnackbar'
import Typography from 'beam/dist/Typography'
import { sortByString } from 'beam/dist/utils/sort'
import { LoadingButton } from 'beam/dist/Button'
import {
  CompactExpansionPanel,
  CompactExpansionPanelSummary,
  CompactExpansionPanelDetails,
} from 'beam/dist/CompactExpansionPanel'
import { Formik, Field } from 'formik'
import translateYup from '../../../../Helpers/Yup'
import { FormTextField, FormDatePickerField } from '../../../Forms'
import { FormControl, FormLabel } from 'beam/dist/Form'
import { METADATA_TYPE } from './metadata.constants'

const autoQaIdPrefix = 'MetadataEditor-'

const DETAIL_WIDTH = 8 * 33 // our value that we can change
const INPUT_PADDING = 14 * 2 // from Material-UI
const BUTTON_WIDTH = 32 // also from Material-UI

const styles = theme => ({
  snackbarError: {
    backgroundColor: theme.palette.error.main,
  },
  expansionRoot: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    background: 'none',
    '&:before': {
      display: 'none',
    },
  },
  expansionSummaryRoot: {
    padding: theme.spacing(0, 1),
    background: 'none',
  },
  expansionSummaryExpanded: {
    background: 'none',
  },
  expansionDetailsRoot: {
    padding: theme.spacing(1, 0, 0),
    background: 'none',
  },
  details: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  detailsList: {
    flex: `0 0 ${DETAIL_WIDTH}px`,
    maxWidth: DETAIL_WIDTH,
    padding: theme.spacing(0.5),
    paddingTop: 0,
  },
  detailKey: {
    display: 'block',
  },
  disabledDateField: {
    opacity: 0.8,
    pointerEvents: 'none',
  },
  inputLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: DETAIL_WIDTH - INPUT_PADDING - theme.spacing(2), // don't extend past outline
  },
  inputLabelWithButton: {
    maxWidth: DETAIL_WIDTH - INPUT_PADDING - BUTTON_WIDTH - theme.spacing(2), // don't extend over icon button
  },
  shrunkInputLabel: {
    maxWidth: DETAIL_WIDTH - theme.spacing(2), // don't be wider than the input
    backgroundColor: theme.palette.background.default, // the 'notch' won't be wide enough, so cover the border
  },
  metadataTextFieldFormControl: {
    width: '100%',
  },
})

class MetadataEditor extends Component {
  static propTypes = {
    contact: PropTypes.object.isRequired,
    canEditMetadata: PropTypes.bool,
    t: PropTypes.func.isRequired,
    classes: PropTypes.object.isRequired,
    dialogError: PropTypes.object,
    handleSubmission: PropTypes.func,
  }

  static defaultProps = {
    canEditMetadata: true,
    dialogError: null,
    handleSubmission: () => {},
  }

  state = {
    submissionError: null,
    isErrorsOpen: false,
    isAlreadySaved: false,
  }

  toggleErrors = isErrorsOpen => this.setState({ isErrorsOpen })
  showErrors = () => this.toggleErrors(true)
  hideErrors = () => this.toggleErrors(false)

  setSubmissionError = submissionError => this.setState({ submissionError })

  displayError = () => {
    const error = this.state.submissionError
    const errorMessage =
      error.response && error.response.data && error.response.data.errorMessage
        ? error.response.data.errorMessage
        : error.message
    return <span>{errorMessage}</span>
  }

  submitMetadata = async (values, actions) => {
    const submittedValues = values

    try {
      await this.props.handleSubmission(submittedValues)
      this.setState({ isAlreadySaved: true })
      actions.resetForm(submittedValues)
    } catch (e) {
      this.showErrors()
      this.setSubmissionError(e)
    } finally {
      actions.setSubmitting(false)
    }
  }

  getInitialValues = metadataFields =>
    metadataFields.reduce(
      (vals, metadata) => ({
        ...vals,
        [metadata.metadataKey]: metadata.value ? metadata.value : metadata.type === METADATA_TYPE.DATE ? null : '',
      }),
      {},
    )

  getFieldValidationSchema = field => {
    const Yup = translateYup(this.props.t)
    const maxLength = 2056
    switch (field.type) {
      case METADATA_TYPE.NUMBER:
        return Yup.number().nullable()
      case METADATA_TYPE.DATE:
        return Yup.date().nullable()
      default:
        //TEXT and HYPERLINK
        return Yup.string()
          .max(maxLength)
          .nullable()
    }
  }

  getValidationSchema = metadataFields => {
    const Yup = translateYup(this.props.t)

    const validationFieldSchema = metadataFields.reduce(
      (schema, field) => ({
        ...schema,
        [field.metadataKey]: this.getFieldValidationSchema(field),
      }),
      {},
    )

    return Yup.object().shape(validationFieldSchema)
  }

  getMetadataFields() {
    const metadata = this.props.contact.metadata
    const metadataKeys = metadata ? Object.keys(metadata) : []
    return metadataKeys.length
      ? metadataKeys
        .map(metadataKey => ({
          metadataKey,
          ...metadata[metadataKey],
        }))
        .sort((a, b) => sortByString(a.metadataKey.toLowerCase(), b.metadataKey.toLowerCase()))
      : []
  }

  renderField = field => {
    switch (field.type) {
      case METADATA_TYPE.DATE: {
        return this.renderDateField(field)
      }
      case METADATA_TYPE.HYPERLINK: {
        return this.renderHyperlinkField(field)
      }
      default: {
        return this.renderTextField(field)
      }
    }
  }

  MetadataTextField = ({ label, ...textFieldProps }) => {
    return (
      <FormControl classes={{ root: this.props.classes.metadataTextFieldFormControl }}>
        {label && <FormLabel>{label}</FormLabel>}
        <FormTextField {...textFieldProps} />
      </FormControl>
    )
  }

  handleLink = url => () => window.open(!/^https?:\/\//i.test(url) ? `http://${url}` : url, '_blank')

  renderDateField = ({ metadataKey, name, readOnly }) => (
    <Field
      label={name}
      name={metadataKey}
      datePickerProps={{
        format: 'MM/DD/YYYY',
        'auto-qa-id': `${autoQaIdPrefix}date-${metadataKey}`,
        'data-testid': `${autoQaIdPrefix}date-${metadataKey}`,
      }}
      component={FormDatePickerField}
      classes={{
        root: `${this.props.classes.metadataTextFieldFormControl} ${this.props.classes.formControl} ${
          !this.props.canEditMetadata || readOnly ? this.props.classes.disabledDateField : ''
        }`,
      }}
    />
  )

  renderHyperlinkField = field => {
    const { classes } = this.props

    return (
      <Field
        label={field.name}
        name={field.metadataKey}
        textFieldProps={{
          disabled: !this.props.canEditMetadata || field.readOnly,
          'auto-qa-id': `${autoQaIdPrefix}link-${field.metadataKey}`,
          'data-testid': `${autoQaIdPrefix}link-${field.metadataKey}`,
          InputProps: {
            endAdornment: (
              <IconButton
                auto-qa-id={`${autoQaIdPrefix}link-${field.metadataKey}-icon`}
                data-testid={`${autoQaIdPrefix}link-${field.metadataKey}-icon`}
                onClick={this.handleLink(field.value)}
              >
                <BeamIcon>LinkVariant</BeamIcon>
              </IconButton>
            ),
          },
          inputProps: {
            'data-lpignore': true,
          },
          InputLabelProps: {
            classes: {
              root: classNames(classes.inputLabel, classes.inputLabelWithButton),
              shrink: classes.shrunkInputLabel,
            },
          },
        }}
        component={this.MetadataTextField}
        classes={{ root: classes.formControl }}
      />
    )
  }

  renderTextField = ({ metadataKey, name, readOnly }) => {
    const { classes } = this.props

    return (
      <Field
        label={name}
        name={metadataKey}
        textFieldProps={{
          disabled: !this.props.canEditMetadata || readOnly,
          'auto-qa-id': `${autoQaIdPrefix}text-${metadataKey}`,
          'data-testid': `${autoQaIdPrefix}text-${metadataKey}`,
          inputProps: {
            'data-lpignore': true,
          },
          InputLabelProps: {
            classes: {
              root: classes.inputLabel,
              shrink: classes.shrunkInputLabel,
            },
          },
        }}
        component={this.MetadataTextField}
        classes={{ root: classes.formControl }}
      />
    )
  }

  isTouched = touched => Object.values(touched).some(val => !!val)

  renderMetadataForm(metadataFields) {
    const { classes, t, canEditMetadata } = this.props
    const { isAlreadySaved } = this.state

    const initialValues = this.getInitialValues(metadataFields)
    const validationSchema = this.getValidationSchema(metadataFields)

    return (
      <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={this.submitMetadata}>
        {({ handleSubmit, isValid, dirty, isSubmitting, touched }) => {
          return (
            <form data-testid={`${autoQaIdPrefix}form`} onSubmit={handleSubmit}>
              <div className={classes.details}>
                {metadataFields.map(field => (
                  <div className={classes.detailsList} key={field.metadataKey}>
                    {this.renderField(field)}
                  </div>
                ))}
              </div>
              <div>
                <LoadingButton
                  type="submit"
                  auto-qa-id={`${autoQaIdPrefix}submit`}
                  data-testid={`${autoQaIdPrefix}submit`}
                  loading={isSubmitting}
                  disabled={isSubmitting || !isValid || !dirty || !canEditMetadata}
                  fullWidth
                  variant="contained"
                >
                  {!this.isTouched(touched) && isAlreadySaved
                    ? [<BeamIcon key={1}>Check</BeamIcon>, t('saved')]
                    : t(['saveMetadataLabel', 'Save Metadata'])}
                </LoadingButton>
              </div>
            </form>
          )
        }}
      </Formik>
    )
  }

  render() {
    const { classes, t } = this.props
    const { isErrorsOpen, submissionError } = this.state

    const metadataFields = this.getMetadataFields()

    return (
      <Fragment>
        <Snackbar
          open={isErrorsOpen}
          onClose={this.hideErrors}
          ContentProps={{
            className: classes.snackbarError,
          }}
          message={submissionError && this.displayError(submissionError)}
          auto-qa-id={`${autoQaIdPrefix}errors-snackbar`}
          data-testid={`${autoQaIdPrefix}errors-snackbar`}
          action={
            <IconButton
              auto-qa-id={`${autoQaIdPrefix}errors-close`}
              data-testid={`${autoQaIdPrefix}errors-close`}
              key="close"
              aria-label={t('close')}
              color="inherit"
              onClick={this.hideErrors}
            >
              <BeamIcon>Close</BeamIcon>
            </IconButton>
          }
        />
        <CompactExpansionPanel disabled={metadataFields.length === 0} classes={{ root: classes.expansionRoot }}>
          <CompactExpansionPanelSummary
            IconButtonProps={{
              'auto-qa-id': `${autoQaIdPrefix}expand-button`,
              'data-testid': `${autoQaIdPrefix}expand-button`,
            }}
            classes={{
              root: classes.expansionSummaryRoot,
              expanded: classes.expansionSummaryExpanded,
            }}
            expandIcon={<BeamIcon>ChevronDown</BeamIcon>}
          >
            <Typography variant="body2">
              <strong>{t('metadata')}</strong>
            </Typography>
          </CompactExpansionPanelSummary>
          <CompactExpansionPanelDetails classes={{ root: classes.expansionDetailsRoot }}>
            {this.renderMetadataForm(metadataFields)}
          </CompactExpansionPanelDetails>
        </CompactExpansionPanel>
      </Fragment>
    )
  }
}

export { MetadataEditor as UnstyledMetadataEditor }

export default withTranslation()(withStyles(styles)(MetadataEditor))
